// src/components/overview/NavigationMenu.js
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../company-management/hooks/usePermissions';
import { PERMISSIONS } from '../../company-management/constants/permissions';
import { DEV_TOOLS_PATH, isDevToolsEnabled } from '../../dev-tools/constants';
import {
  DashboardIcon,
  CalendarIcon,
  ArchiveIcon,
  PeopleIcon,
  ManageAccountsIcon,
  SettingsIcon,
  SupportIcon,
  LogoutIcon,
  DevToolsIcon,
} from './icons/NavIcons';
import './styles/NavigationMenu.css';
import { useAppEmailVerificationRestricted } from '../email-verification/EmailVerificationRestrictionContext';
import { APP_EMAIL_VERIFICATION_LOCK_TITLE } from '../email-verification/appEmailVerificationRestriction';
import '../email-verification/appEmailVerificationLock.css';

const NavMenuItem = ({ icon: Icon, label, onClick, isActive, permission, section, disabled = false }) => {
  if (!permission) return null;

  return (
    <li>
      <button
        type="button"
        className={`nav-menu-item ${isActive ? 'active' : ''}${disabled ? '' : ' nav-menu-item--allowed'}`}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        data-section={section}
        aria-current={isActive ? 'page' : undefined}
        title={disabled ? APP_EMAIL_VERIFICATION_LOCK_TITLE : undefined}
      >
        <span className="nav-menu-item-icon" aria-hidden>
          <Icon />
        </span>
        <span className="nav-menu-item-label">{label}</span>
      </button>
    </li>
  );
};

const NavigationMenu = ({ handleMenuClose, handleSignOut }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const navigationLocked = useAppEmailVerificationRestricted();
  const showDevTools = isDevToolsEnabled(user?.isSpecialAdmin);

  const goTo = (path) => {
    navigate(path);
    handleMenuClose();
  };

  const isNavDisabled = (section) => navigationLocked && section !== 'overview';

  return (
    <nav className={`nav-menu${navigationLocked ? ' nav-menu--locked' : ''}`} aria-label="Main navigation">
      <ul className="nav-menu-list">
        <NavMenuItem
          icon={DashboardIcon}
          label="Overview"
          onClick={() => goTo('/app/overview')}
          isActive={location.pathname === '/app/overview'}
          permission={hasPermission(PERMISSIONS.OVERVIEW.VIEW)}
          section="overview"
        />
        <NavMenuItem
          icon={CalendarIcon}
          label="Calendar"
          onClick={() => goTo('/app/overview/calendar')}
          isActive={location.pathname === '/app/overview/calendar'}
          permission={hasPermission(PERMISSIONS.CALENDAR.VIEW)}
          section="calendar"
          disabled={isNavDisabled('calendar')}
        />
        <NavMenuItem
          icon={ArchiveIcon}
          label="History"
          onClick={() => goTo('/app/overview/history')}
          isActive={location.pathname === '/app/overview/history'}
          permission={hasPermission(PERMISSIONS.HISTORY.VIEW)}
          section="history"
          disabled={isNavDisabled('history')}
        />
        <NavMenuItem
          icon={PeopleIcon}
          label="Client Management"
          onClick={() => goTo('/app/overview/clients')}
          isActive={location.pathname.startsWith('/app/overview/clients')}
          permission={hasPermission(PERMISSIONS.CLIENTS.VIEW)}
          section="clients"
          disabled={isNavDisabled('clients')}
        />
        <NavMenuItem
          icon={ManageAccountsIcon}
          label="Employee Management"
          onClick={() => goTo('/app/overview/company-management')}
          isActive={
            location.pathname.startsWith('/app/overview/company-management') ||
            location.pathname === '/app/overview/permissions'
          }
          permission={hasPermission(PERMISSIONS.EMPLOYEES.VIEW)}
          section="employees"
          disabled={isNavDisabled('employees')}
        />
        <NavMenuItem
          icon={SettingsIcon}
          label="Settings"
          onClick={() => goTo('/app/overview/settings')}
          isActive={location.pathname.startsWith('/app/overview/settings')}
          permission={hasPermission(PERMISSIONS.SETTINGS.VIEW)}
          section="settings"
          disabled={isNavDisabled('settings')}
        />
        {showDevTools ? (
          <NavMenuItem
            icon={DevToolsIcon}
            label="Developer tools"
            onClick={() => goTo(DEV_TOOLS_PATH)}
            isActive={location.pathname.startsWith(DEV_TOOLS_PATH)}
            permission
            section="dev-tools"
            disabled={isNavDisabled('dev-tools')}
          />
        ) : null}
        <NavMenuItem
          icon={SupportIcon}
          label="Support"
          onClick={() => goTo('/app/overview/support')}
          isActive={location.pathname === '/app/overview/support'}
          permission={hasPermission(PERMISSIONS.SUPPORT.VIEW)}
          section="support"
          disabled={isNavDisabled('support')}
        />
      </ul>
      <div className="nav-menu-divider" aria-hidden />
      <div className="nav-menu-footer">
        <button type="button" className="nav-signout" onClick={handleSignOut}>
          <span className="nav-signout-icon" aria-hidden>
            <LogoutIcon />
          </span>
          <span className="nav-signout-label">Sign Out</span>
        </button>
      </div>
    </nav>
  );
};

export default NavigationMenu;
